// src/components/candidateProfile/ProfilePortfolio.js
import React, { useState } from 'react';
import { BookOpen, GraduationCap, Image, Film, Play, X } from 'lucide-react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom'; 

export default function ProfilePortfolio({ 
  tp, languagesList, skillsList, educations, portfolioPhotos, portfolioVideos,
  filmsCount, webSeriesCount, tvShowsCount, adsCount, theatreCount, estimatedYears,
  navigateToEditStep,isReadOnly = false 
}) {
  const navigate = useNavigate(); 
  
  // Modal State for Video Playback
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState('');

  // 🟢 Modal State for Photo Lightbox Preview
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [activePhotoUrl, setActivePhotoUrl] = useState('');

  const handlePlayVideo = (url) => {
    setActiveVideoUrl(url);
    setShowVideoModal(true);
  };

  // 🟢 Open Photo Modal on Click
  const handleViewPhoto = (url) => {
    setActivePhotoUrl(url);
    setShowPhotoModal(true);
  };

  return (
    <div className="col-12 col-lg-6 d-flex flex-column gap-4">

      {/* Biography */}
      <div className="card-dark">
        <div className="fw-bold mb-2">Biography & About</div>
        <p className="cp-bio mb-0">{tp.aboutYourself || "No biography provided yet."}</p>
      </div>

      {/* Languages */}
      <div className="card-dark">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="fw-bold text-white">Languages Known</div>
          {!isReadOnly && navigateToEditStep && (
              <span className="cp-custom-action-btn" onClick={() => navigateToEditStep(1)}>
                  + Add Language
              </span>
          )}
        </div>
        <div className="table-responsive">
          <table className="table table-dark table-borderless align-middle mb-0 cp-table">
            <thead className="text-muted">
              <tr className="border-bottom border-secondary">
                <th>Language</th>
                <th className="text-center">Speak</th>
                <th className="text-center">Read</th>
                <th className="text-center">Write</th>
              </tr>
            </thead>
            <tbody>
              {languagesList.map((item, idx) => (
                <tr key={idx} className="border-bottom border-secondary">
                  <td><span className="bg-success cp-bullet"></span>{item.l}</td>
                  <td className="text-center">{item.s}</td>
                  <td className="text-center">{item.r}</td>
                  <td className="text-center">{item.w}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Talents & Skills */}
      <div className="card-dark">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="fw-bold text-white">Talents & Skills</div>
          {!isReadOnly && navigateToEditStep && (
              <span className="cp-custom-action-btn" onClick={() => navigateToEditStep(1)}>
                  + Add Skill
              </span>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {skillsList.length > 0 ? skillsList.map((item, index) => (
            <div key={index} className="badge badge-outline border-secondary text-white d-flex align-items-center gap-2 px-3 py-2">
              <span>{item.icon}</span> {item.name}
            </div>
          )) : <span className="text-muted small">No talents specified.</span>}
        </div>
      </div>
      
      {/* Experience & Training */}
      <div className="row g-4">
        <div className="col-12 col-md-6">
          <div className="card-dark h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="fw-bold text-white">Experience</div>
              {!isReadOnly && navigateToEditStep && (
                  <span className="cp-custom-action-btn" onClick={() => navigateToEditStep(2)}>
                      + Add Experience
                  </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-4">
              <div className="cp-exp-badge">
                <span className="cp-exp-badge-num">{estimatedYears}</span>
                <span className="cp-exp-badge-unit">Years</span>
              </div>
              <div className="flex-grow-1">
                <ul className="cp-list cp-experience-details-list">
                  <li className="cp-list-item"><span className="text-muted">Films</span><span className="text-white">{filmsCount} Project{filmsCount !== 1 ? 's' : ''}</span></li>
                  <li className="cp-list-item"><span className="text-muted">Web Series</span><span className="text-white">{webSeriesCount} Project{webSeriesCount !== 1 ? 's' : ''}</span></li>
                  <li className="cp-list-item"><span className="text-muted">TV Shows</span><span className="text-white">{tvShowsCount} Project{tvShowsCount !== 1 ? 's' : ''}</span></li>
                  <li className="cp-list-item"><span className="text-muted">Commercial Ads</span><span className="text-white">{adsCount} Ad{adsCount !== 1 ? 's' : ''}</span></li>
                  <li className="cp-list-item"><span className="text-muted">Theatre</span><span className="text-white">{theatreCount} Play{theatreCount !== 1 ? 's' : ''}</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="card-dark h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="fw-bold text-white">Training & Education</div>
              {!isReadOnly && navigateToEditStep && (
                  <span className="cp-custom-action-btn" onClick={() => navigateToEditStep(3)}>
                      + Add Education
                  </span>
              )}
            </div>
            <div className="d-flex flex-column gap-3 cp-education-details-list">
              {educations.length > 0 ? educations.map((edu, idx) => (
                <div key={idx} className="cp-edu-item">
                  <div className="cp-edu-icon">
                    {edu.courseDegree?.toLowerCase().includes('degree') || edu.courseDegree?.toLowerCase().includes('bachelor') || edu.courseDegree?.toLowerCase().includes('diploma') ? (
                      <GraduationCap size={16}/>
                    ) : (
                      <BookOpen size={16}/>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div className="text-white fw-medium">{edu.courseDegree || 'Course/Degree'}</div>
                    <div className="text-muted cp-edu-meta">{edu.instituteName || 'Institute Name'} {edu.cityTown ? `• ${edu.cityTown}` : ''}</div>
                  </div>
                  <div className="text-muted">{edu.endYear || edu.startYear || '---'}</div>
                </div>
              )) : (
                <div className="text-muted small py-3">No education or training added yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 🟢 UNIFIED PORTFOLIO MEDIA GALLERY (Single Row Horizontal Scroll) */}
      <div className="card-dark animate-fade-in">
        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom border-secondary pb-3">
          <div className="fw-bold text-white d-flex align-items-center gap-2">
            <Image size={18} className="text-danger" /> Portfolio Media ({portfolioPhotos.length} Photos, {portfolioVideos.length} Videos)
          </div>
          {!isReadOnly && navigateToEditStep && (
            <span className="cp-custom-action-btn" onClick={() => navigateToEditStep(4)}>
                Edit Portfolio
            </span>
          )}
        </div>

        {/* 🟢 Horizontal Scroll Row Container */}
        <div className="d-flex gap-3 overflow-x-auto pb-2 cp-scroll-gallery">
          {/* Photos list */}
          {portfolioPhotos.map((src, i) => (
            <div 
              key={`photo-${i}`} 
              className="cp-photo-wrapper flex-shrink-0" 
              style={{ width: '135px', height: '135px' }}
              onClick={() => handleViewPhoto(src)}
            >
              <img src={src} className="w-100 h-100 object-fit-cover rounded" alt={`Portfolio Photo ${i + 1}`}/>
            </div>
          ))}

          {/* Videos list */}
          {portfolioVideos.map((src, i) => (
            <div 
              key={`video-${i}`} 
              className="cp-video-thumbnail-card flex-shrink-0" 
              style={{ width: '135px', height: '135px' }}
              onClick={() => handlePlayVideo(src)}
            >
              <div className="cp-video-overlay">
                <div className="cp-video-play-btn">
                  <Play size={18} fill="currentColor" />
                </div>
                <span className="text-white mt-1 small fw-bold" style={{ fontSize: '9px' }}>Play Video</span>
              </div>
              <video src={src} className="w-100 h-100 object-fit-cover rounded" muted />
            </div>
          ))}

          {/* Empty State */}
          {portfolioPhotos.length === 0 && portfolioVideos.length === 0 && (
            <div className="text-muted small py-3 w-100 text-center">
              No portfolio photos or videos uploaded yet.
            </div>
          )}
        </div>
      </div>

      {/* 🟢 PREMIUM PHOTO LIGHTBOX MODAL */}
      <Modal 
        show={showPhotoModal} 
        onHide={() => setShowPhotoModal(false)} 
        centered 
        contentClassName="bg-dark border-0 rounded-4 overflow-hidden shadow-lg"
      >
        <Modal.Header className="border-secondary bg-black p-3 d-flex align-items-center justify-content-between">
          <Modal.Title className="text-white fs-6 d-flex align-items-center gap-2">
            <Image size={18} className="text-danger" /> Portfolio Photo Preview
          </Modal.Title>
          <Button variant="dark" className="p-1 rounded-circle border-secondary bg-transparent" onClick={() => setShowPhotoModal(false)}>
            <X size={18} className="text-white" />
          </Button>
        </Modal.Header>
        <Modal.Body className="p-2 bg-black text-center d-flex align-items-center justify-content-center" style={{ minHeight: '350px', maxHeight: '80vh' }}>
          {activePhotoUrl ? (
            <img src={activePhotoUrl} className="mw-100 mh-100 object-fit-contain rounded" style={{ maxHeight: '70vh' }} alt="Expanded Portfolio Photo" />
          ) : (
            <p className="text-white p-4">Loading photo...</p>
          )}
        </Modal.Body>
      </Modal>

      {/* 🟢 PREMIUM DARK LIGHTBOX THEATER MODAL (For Video Playback) */}
      <Modal 
        show={showVideoModal} 
        onHide={() => setShowVideoModal(false)} 
        centered 
        contentClassName="bg-dark border-0 rounded-4 overflow-hidden shadow-lg"
      >
        <Modal.Header className="border-secondary bg-black p-3 d-flex align-items-center justify-content-between">
          <Modal.Title className="text-white fs-6 d-flex align-items-center gap-2">
            <Film size={18} className="text-danger" /> Audition Video Playback
          </Modal.Title>
          <Button variant="dark" className="p-1 rounded-circle border-secondary bg-transparent" onClick={() => setShowVideoModal(false)}>
            <X size={18} className="text-white" />
          </Button>
        </Modal.Header>
        <Modal.Body className="p-0 bg-black text-center" style={{ height: '420px' }}>
          {activeVideoUrl ? (
            <video src={activeVideoUrl} className="w-100 h-100 object-fit-contain" controls autoPlay />
          ) : (
            <p className="text-white p-4">Loading video...</p>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
}